import React, {useRef, useState} from 'react';
import { Box, Card, CardContent, Container, Divider, Grid, TextField, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { TypographyH1 } from '../../../assets/typographyTheme';
import { Button } from '../../Button';
import { FormLabel,
        helperTextStyles, 
        textFieldStyles, 
        validateEmail, 
        validateName, 
        validateRequiredField, 
        validateZipcode } from '../../Onboarding/Helper';
import { validateInvoiceNumber, validatePaymentAmount } from '../Helper';
import useAddPaymentMethod from '../../../hooks/useAddPaymentMethod';
import { ModalComponent } from '../../ModalComponent';
import { PAYMENT_OPTIONS } from '../../../constants';
import { invoiceLookupApi} from "../../../utils/OrderService/OrderApi";
import { AlertMessage } from "../../AlertMessage";
import { OrderErrorCodes } from "../../../utils/OrderService/errorCodes";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { formatDate } from 'date-fns/format';

const RequiredSymbol = styled('span')({
    color: 'red',
});

const buttonsContainerSxStyles = {
    flexDirection: {xs: 'column', sm:'row'},
    display:"flex",
    justifyContent:'left',
    marginY: "24px",
}

const cardStyles = {
    variant:"outlined",
    bgcolor: 'neutral.white',
    borderRadius: '16px',
    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.50)'
};

export const InvoicePayForm = ({
    handleErrorMsg,
    invoicePayLookupEnabled,
    setShowSpinner
}) => {
    const defaultInputFields = {
        firstName: '',
        lastName: '',
        email: '',
        invoiceNumber: '',
        zipcode: '',
        shippingOrgName: '', // TODO in [MD-46]: delete key and usages once we update backend required fields
        paymentAmount: '', // TODO in [MD-46]: delete key and usages once we update backend required fields
    };
    
    const [errors, setErrors] = useState({});
    const [inputFields, setInputFields] = useState(defaultInputFields);
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [paymentAmount, setPaymentAmount] = useState('');  // TODO in [MD-46]: fetch from BE rather than as an input
    const [errorMsg, setErrorMsg] = useState('');
    const [invoice, setInvoice] = useState(null);
    const alertRef = useRef(null);

    const {
        showPaymentForm,
        processing,
        StripeForm,
        handleClosePaymentModal,
        setPaymentType,
        onPayByInvoice
    } = useAddPaymentMethod(handleErrorMsg);

    const fieldValidations = {
        firstName: validateName,
        lastName: validateName,
        email: validateEmail,
        invoiceNumber: validateInvoiceNumber,
        zipcode: validateZipcode,
        shippingOrgName: validateRequiredField,
        paymentAmount: validatePaymentAmount
    }

    if (invoicePayLookupEnabled) {
        delete fieldValidations.shippingOrgName;
        delete fieldValidations.paymentAmount;
    }    

    const validateInputFields = (field, value) => {
        const validationFunction = fieldValidations[field];
        return validationFunction ? validationFunction(value) : "";
    };

    const errorMsgHandler = (errorMsg) => {
        setErrorMsg(errorMsg);
        alertRef.current?.openAlert(errorMsg);
    }

    const formatToUSD = (value) => {
        // Remove any non-numeric characters except period
        const number = parseFloat(value.replace(/[^0-9.]/g, ""));
        if (isNaN(number)) return "";
        
        // Format number to USD style with two decimal places
        return `$${number.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
    };

    const formatDueDate = (date) => {
        // to parse the high precision ISO string we get from the backend
        const normalizeToMilliseconds = (input) => {
            return input.replace(
                /\.(\d{3})\d*Z$/,
                '.$1Z'
            );
        };

        return formatDate(normalizeToMilliseconds(date), "MMM do, yyyy")
    }
    

    const handleChange = (event) => {
        const { name, value } = event.target;
        const newInputFields = { ...inputFields, [name]: ["firstName", "lastName"].includes(name) ? value : value.trim() };
        setInputFields(newInputFields);
    };

    const handleBlur = (event) => {
        const { name, value } = event.target;
        const errorMsg = validateInputFields(name, value);
        setErrors({ ...errors, [name]: errorMsg });

        if (["firstName", "lastName"].includes(name)) {
            // trim after the fact, in case name has spaces in it
            setInputFields({ ...inputFields, [name]: value.trim() });
        }

        if (!invoicePayLookupEnabled && !errorMsg && name === "paymentAmount") {
            const formattedValue = formatToUSD(value);
            setInputFields({ ...inputFields, [name]: formattedValue });
        }
    };

    const createTextField = (key, label, value, indicatorNumber) => {
        return (
            <>
                <Box display='flex' alignItems='center'>
                { indicatorNumber && <Box
                    sx={{
                        width: 30,
                        height: 30,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '50%',
                        backgroundColor: '#079AE0',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: 18,
                        mr: 1,
                        mb: 1
                    }}
                    >
                       {indicatorNumber}
                    </Box>
                    }
                    <FormLabel label={label} data-testid={key} />
                </Box>
                <TextField
                    id={key}
                    name={key}
                    data-testid={key}
                    type="text"
                    value={value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    focused
                    variant="outlined"
                    fullWidth
                    error={Boolean(errors[key])}
                    helperText={errors[key]}
                    sx={textFieldStyles(errors[key])}
                    FormHelperTextProps={helperTextStyles}
                    inputProps={{ 'aria-label': `${key}` }}
                />
            </>
        )
    };

    const InfoText = ({ text, fontWeight = '400', display='inline-block' }) => {
        return (
            <Typography color='#383D47' fontSize='14px' fontWeight={fontWeight} paddingTop='7px' display={display}>
                {text}
            </Typography>
        );
    };

    const continueToPayment = async () => {
        setInvoiceNumber(inputFields.invoiceNumber);
        setPaymentAmount(invoicePayLookupEnabled ? formatCurrency(invoice.total) : inputFields.paymentAmount);
        setPaymentType(PAYMENT_OPTIONS.PAY_INVOICE);

        const customerDetails = {
            email: inputFields.email,
            name: `${inputFields.firstName} ${inputFields.lastName}`,
            metadata: {
                zipcode: inputFields.zipcode,
                organizationName: inputFields.shippingOrgName
            }
        };
        await onPayByInvoice(customerDetails);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const validationErrors = {};

        Object.keys(inputFields).forEach((fieldName) => {
            const errorMsg = validateInputFields(fieldName, inputFields[fieldName]);
            if (errorMsg) {
                validationErrors[fieldName] = errorMsg;
            }
        });

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        if(invoicePayLookupEnabled) {
            const invoiceDetails = {
                search: {
                    firstName: inputFields.firstName,
                    lastName: inputFields.lastName,
                    email: inputFields?.email,
                    invoiceId: inputFields.invoiceNumber,
                    zipCode: inputFields.zipcode
                }
            }
            try {
                const response = await invoiceLookupApi(invoiceDetails);
                if (response.status === 200) {
                    setInvoice(response.data.invoice);
                }
            } catch (error) {
                if(error?.response?.data?.errorCode === OrderErrorCodes.INVOICES.RESOURCE_NOT_FOUND){
                    setErrors({'invoiceNumber': getErrorMessage(OrderErrorCodes.INVOICES.RESOURCE_NOT_FOUND)});
                }
                else if(error?.response?.data?.errorCode === OrderErrorCodes.INVOICES.INVOICE_ZIP_MISMATCH) {
                    setErrors({'zipcode': getErrorMessage(OrderErrorCodes.INVOICES.INVOICE_ZIP_MISMATCH)});
                }
                else {
                    //using default error message for other error codes
                    errorMsgHandler(getErrorMessage());
                }
            }
        }
        else {
            await continueToPayment()
        }
    };

    const handleCloseContinueToPaymentModal = () => {
        setInvoice(null);
    }

    const handleCloseStripeModal = () => {
        handleClosePaymentModal();
        handleCloseContinueToPaymentModal();
    }

    const formatCurrency = (value) => {
        // invoices are currently only in USD
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value / 100);
    };

    const ContinueToPaymentModalContent = () => (
        <Box display="flex" flexDirection="column" justifyContent="space-between" height={540}>
            <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" fontSize="24px" fontWeight={700}>Pay Invoice #{invoice.invoiceId}</Box>
                <Box sx={{ mt: 3 }} display="flex" fontWeight={700}>Invoice #{invoice.invoiceId}</Box>
                <Box display="flex" justifyContent="space-between">
                    <Box>Item Subtotal</Box>
                    <Box>{formatCurrency(invoice.subtotal)}</Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                    <Box>Shipping</Box>
                    <Box>Free</Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                    <Box>Taxes</Box>
                    <Box>{formatCurrency(invoice.taxes)}</Box>
                </Box>
                {
                    invoice.discounts && invoice.discounts.length > 0 && (
                        <Box display="flex" justifyContent="space-between">
                            <Box>Discount if paid before {formatDueDate(invoice.discounts[0].discountExpiration)}</Box>
                            <Box>-{formatCurrency(invoice.discounts[0].discountAmount)}</Box>
                        </Box>
                    )
                }
                <Divider />
                <Box display="flex" justifyContent="space-between" fontWeight={700}>
                    <Box>Total</Box>
                    <Box>{formatCurrency(invoice.total)}</Box>
                </Box>
            </Box>
            <Box display="flex" justifyContent="center" gap={1}>
                <Button
                    buttonType='mds-secondary'
                    sx={{ textTransform: 'none' }}
                    onClick={handleCloseContinueToPaymentModal}
                >
                    Cancel
                </Button>
                <Button
                    buttonType='mds-primary'
                    sx={{ textTransform: 'none' }}
                    onClick={async () => {
                        await continueToPayment();
                        handleCloseContinueToPaymentModal();
                    }}
                >
                    Continue to payment
                </Button>
            </Box>
        </Box>
    );

    return (
        <>
            <AlertMessage
                variant="filled"
                type="error"
                message={errorMsg}
                sx={{ top: 120 }}
                ref={alertRef}
            />
            <Box paddingTop='16px' display='flex' flexDirection='row' gap={2} maxWidth={'450px'} >
                <Card sx={cardStyles}>
                    <CardContent sx={{ paddingLeft: '0px', paddingRight: '0px' }}>
                        <Container>
                            <Box display='flex' flexDirection='column' gap={'24px'} >
                                <TypographyH1>Pay an invoice</TypographyH1>
                                <Typography><RequiredSymbol>*</RequiredSymbol>All fields are required</Typography>
                                <form onSubmit={handleSubmit} >
                                    <Grid container rowSpacing={'16px'} columnSpacing={'16px'} justifyContent="start" alignItems="flex-start" style={{ marginBottom: '8px' }}>
                                        <Grid item xs={12} sm={6} >
                                            {createTextField("firstName", "Your first name", inputFields.firstName)}
                                        </Grid>
                                        <Grid item xs={12} sm={6} >
                                            {createTextField("lastName", "Your last name", inputFields.lastName)}
                                        </Grid>
                                    </Grid>
                                    <Grid container rowSpacing={'16px'} columnSpacing={'16px'} justifyContent="start" alignItems="flex-start">
                                        <Grid item xs={12} >
                                            {createTextField("email", "Your email address", inputFields.email)}
                                        </Grid>
                                        <Grid item xs={12} >
                                            {createTextField("invoiceNumber", "Invoice number", inputFields.invoiceNumber, 1)}
                                        </Grid>
                                        <Grid item xs={12} >
                                            {createTextField("zipcode", "Billing ZIP code", inputFields.zipcode, 2)}
                                            <InfoText text='Must match the billing ZIP code on the invoice. Accepted formats for billing zip code are 5 digits (e.g. 10001) or 9 digits (e.g. 10001-1111)' />
                                        </Grid>
                                        {!invoicePayLookupEnabled &&
                                            <>
                                                <Grid item xs={12} gap={'7px'}>
                                                    {createTextField("shippingOrgName", "Organization name on the shipping address", inputFields.shippingOrgName, 3)}
                                                    <InfoText text='Must match the shipping address as it appears on the invoice.' />
                                                </Grid>
                                                <Grid item xs={12} >
                                                    {createTextField("paymentAmount", "Payment amount", inputFields.paymentAmount)}
                                                    <InfoText text="Enter the full amount due." fontWeight='700' /> 
                                                    <InfoText text=" Please note we do not accept partial payments." display='inline'/>
                                                </Grid>
                                            </>
                                        }
                                    </Grid>
                                    <Grid sx={buttonsContainerSxStyles}>
                                        <Button 
                                            type="submit"
                                            id="submit"
                                            sx={{ padding: "16px 24px", textTransform: "none" }}
                                            buttonType="mds-primary"
                                            data-testid='continueBtn'
                                            loading={processing}
                                        >
                                            Continue
                                        </Button>
                                    </Grid>
                                </form>
                            </Box>
                        </Container>
                    </CardContent>
                </Card>
            </Box>

            {showPaymentForm && <ModalComponent
                openModal={showPaymentForm}
                closeModalHandler={handleCloseStripeModal}
                modalContent={
                    <>
                        <Box display="flex" alignItems="center" fontSize="24px" fontWeight={700}>Pay Invoice #{invoiceNumber}</Box>
                        <StripeForm invoiceNumber={invoiceNumber} paymentAmount={paymentAmount} setShowSpinner={setShowSpinner} handleCancel={handleCloseStripeModal} />
                    </>
                }
                style={{ 
                    maxWidth: '521px',
                    width: 'auto',
                    padding: '24px'
                }}
            />}

            {!!invoice && <ModalComponent
                openModal={!!invoice}
                closeModalHandler={handleCloseContinueToPaymentModal}
                modalContent={
                    <ContinueToPaymentModalContent />
                }
                style={{ 
                    maxWidth: '521px',
                    width: 'auto',
                    padding: '24px'
                }}
            />}
        </>
    );
};